import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Flex, Group, Image, Paper, Stack, Text } from '@mantine/core';
import turfBbox from '@turf/bbox';
import booleanIntersects from '@turf/boolean-intersects';
import {
  Feature,
  feature,
  FeatureCollection,
  featureCollection,
  Point,
  Polygon,
  polygon,
} from '@turf/helpers';
import mapboxgl, { GeoJSONSource, GeoJSONSourceRaw, LngLatBoundsLike, LngLatLike } from 'mapbox-gl';

import { FONT_WEIGHT_BOLD } from 'constants/mantine';
import MAPBOX_CONSTANTS, { MapMarkers, MARKERS, MARKERS_SELECTED, MODES } from 'constants/mapbox';

import useReactMapbox from 'util/hooks/useReactMapbox';
import removeMapLayer from 'util/mapbox';
import { getDispatchPinColor, getPlanTrackingLegendIcons } from 'util/mapImageryColors';
import showToast from 'actions/toastActions';
import { DispatchFilter, DispatchType } from 'store/dispatch/types';

import { BulkAssignButton } from '../Actions/BulkAssignButton';

import { DispatchMapPopup } from './Popup';

const DEFAULT = 'default';
const BULK_ASSIGN = 'bulk_assign';

type DispatchMapProps = {
  dispatchPlans: DispatchType[];
  refreshPage: VoidFunction;
  filter: DispatchFilter;
  updateFilter: (key: keyof DispatchFilter, value: string | boolean | undefined | null) => void;
};

export const DispatchMap = ({
  dispatchPlans,
  refreshPage,
  filter,
  updateFilter,
}: DispatchMapProps) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const popupContainerRef = useRef<HTMLDivElement>(document.createElement('div'));
  const [popupData, setPopupData] = useState<DispatchType | null>(null);
  const [popupLngLat, setPopupLngLat] = useState<LngLatLike>();

  const drawRef = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [clickMode, setClickMode] = useState<typeof DEFAULT | typeof BULK_ASSIGN>(DEFAULT);
  const [isDrawing, toggleIsDrawing] = useState<boolean>(false);

  const [planIds, setPlanIds] = useState(new Set());

  const initialViewport = {
    latitude: 37,
    longitude: -97,
    zoom: 4,
    width: 0,
    height: 0,
  };
  const { mapContainerRef, mapRef, mapHasLoaded, setViewport } = useReactMapbox({
    initialViewport,
    drawRef,
    showNavigationControls: true,
    showGeocoderSearch: false,
    mapStyle: MAPBOX_CONSTANTS.styleOutdoors,
    setDrawMode: () => {},
    onLoad: (_map) => {
      Object.values(MapMarkers).forEach((marker) => {
        _map.loadImage(marker.image, (error, image) => {
          if (error) {
            throw error;
          }
          if (image) {
            _map.addImage(marker.name, image);
          }
        });
      });
    },
  });
  const map = mapRef.current;

  // Only allows updates when viewed plans change. Prevents unnecessary re-renders
  useEffect(() => {
    const planArrSet = new Set(dispatchPlans.map((samplePlan) => samplePlan.id));
    if (planIds !== planArrSet && mapHasLoaded && map?.getSource('markers')) {
      map?.removeLayer('markers')?.removeSource('markers');
    }
    setPlanIds(planArrSet);
  }, [dispatchPlans, mapHasLoaded, map]);

  // Necessary to prevent extra overlapping mapboxgl.Popup instances from being rendered
  useEffect(() => {
    if (popupData && popupLngLat && popupContainerRef.current) {
      const popup = new mapboxgl.Popup({ closeOnClick: true })
        .setLngLat(popupLngLat)
        .setDOMContent(popupContainerRef.current)
        .addTo(map!);

      popup.on('close', () => {
        setPopupData(null);
        setPopupLngLat(undefined);
      });
    }
  }, [popupData, popupLngLat]);

  useEffect(() => {
    if (mapHasLoaded && dispatchPlans.length && map && !map.getSource('markers')) {
      const popups = document.querySelectorAll('.mapboxgl-popup');
      popups.forEach((popup) => popup.remove());
      const source = {
        type: 'geojson',
        data: featureCollection(
          dispatchPlans.map((data) =>
            feature(data.field_centroid, {
              data,
              pinColor: getDispatchPinColor(data),
            }),
          ),
        ),
      } as GeoJSONSourceRaw;
      map.addLayer({
        id: MARKERS,
        type: 'symbol',
        source: source,
        layout: {
          'icon-image': ['get', 'pinColor'],
          'icon-allow-overlap': true,
        },
      });
      const bbox = turfBbox(source.data) as LngLatBoundsLike;
      map.fitBounds(bbox, {
        padding: 50,
        maxZoom: 10,
      });
      const zoom = map.getZoom();
      setViewport((prevViewport) => ({
        ...prevViewport,
        latitude: initialViewport.latitude,
        longitude: initialViewport.longitude,
        zoom: zoom > 13 ? zoom : 10,
      }));
      map.on('click', MARKERS, (e: any) => {
        const plan = JSON.parse(e.features[0].properties.data);
        setPopupData(plan);
        setPopupLngLat(e.lngLat);
      });
      handleSetClickBehavior(DEFAULT);

      map.on('mouseenter', MARKERS, () => {
        if (map && drawRef.current.getMode() === MODES.SELECT) {
          map.getCanvas().style.cursor = 'pointer';
        }
      });

      map.on('mouseleave', MARKERS, () => {
        if (map && drawRef.current.getMode() === MODES.SELECT) {
          map.getCanvas().style.cursor = '';
        }
      });
    }
  }, [dispatchPlans, map, mapHasLoaded]);

  const handleToggleDraw = (enable: boolean) => {
    if (map) {
      if (enable) {
        drawRef.current?.changeMode(MODES.DRAW_RECTANGLE);
        map.on('draw.create', onCreatePolygon);
        map.getCanvas().style.cursor = 'crosshair';
      } else {
        map.off('draw.create', onCreatePolygon);
        drawRef.current?.changeMode(MODES.STATIC);
        drawRef.current?.deleteAll();
        map.getCanvas().style.cursor = '';
      }
    }
    toggleIsDrawing(enable);
  };

  const handleSetClickBehavior = (mode: typeof DEFAULT | typeof BULK_ASSIGN) => {
    if (map) {
      if (mode === BULK_ASSIGN) {
        map.on('click', MARKERS, onBulkAssignClick);
      } else {
        map.off('click', MARKERS, onBulkAssignClick);
        removeMapLayer(mapRef, MARKERS_SELECTED);
        drawRef.current?.deleteAll();
      }
      setClickMode(mode);
    }
  };

  const setupSelectedMarkersLayer = (markers: FeatureCollection) => {
    const source = {
      type: 'geojson',
      data: markers,
    } as GeoJSONSourceRaw;
    map?.addLayer({
      id: MARKERS_SELECTED,
      type: 'symbol',
      source,
      layout: {
        'icon-image': ['get', 'pinColor'],
        'icon-allow-overlap': true,
      },
    });
  };

  const onBulkAssignClick = useCallback(
    (e: any) => {
      const data = JSON.parse(e.features[0].properties.data);
      if (!data.id) return;

      const newPlanFeat = feature(data.field_centroid, {
        data: data,
        pinColor: MapMarkers.RED_MARKER.name,
      });

      if (map?.getLayer(MARKERS_SELECTED)) {
        const { _data } = map?.getSource(MARKERS_SELECTED) as any;
        const alreadyHighlighted = (_data as FeatureCollection).features.some(
          (feat) => feat?.properties?.data?.id === data.id,
        );
        const zoneAddedOrRemoved = alreadyHighlighted
          ? (_data as FeatureCollection).features.filter(
              (feat) => feat.properties?.data?.id !== data.id,
            )
          : [_data.features, newPlanFeat].flat();
        const highlightLayer = featureCollection(zoneAddedOrRemoved as Feature<Point>[]);
        (map?.getSource(MARKERS_SELECTED) as GeoJSONSource).setData(highlightLayer);
      } else {
        setupSelectedMarkersLayer(featureCollection([newPlanFeat]));
      }
    },
    [map],
  );

  const onCreatePolygon = useCallback(
    (e: FeatureCollection<Polygon>) => {
      try {
        const feat = e.features[0];
        const { _data } = map?.getSource(MARKERS) as any;
        const insideRectangle = (_data as FeatureCollection).features.reduce(
          (featList, planFeat) => {
            if (booleanIntersects(feat, planFeat)) {
              const updatedFeat = {
                ...planFeat,
                properties: {
                  ...planFeat.properties,
                  pinColor: MapMarkers.RED_MARKER.name,
                },
              } as Feature<Point>;
              return featList.concat([updatedFeat]);
            }
            return featList;
          },
          [] as Feature<Point>[],
        );
        const highlightLayer = featureCollection(insideRectangle as Feature<Point>[]);
        if (map?.getLayer(MARKERS_SELECTED)) {
          (map?.getSource(MARKERS_SELECTED) as GeoJSONSource).setData(highlightLayer);
        } else {
          setupSelectedMarkersLayer(highlightLayer);
        }
        drawRef.current?.deleteAll();
        handleToggleDraw(false);
      } catch (err) {
        showToast(err.message);
      }
    },
    [map, drawRef],
  );

  const getSelectedPlans = (): DispatchType[] => {
    try {
      // dangling _ otherwise, just catch it and return []
      const { _data } = map?.getSource(MARKERS_SELECTED) as any;
      return (_data as FeatureCollection).features.reduce(
        (list, feat) => (feat.properties?.data ? [...list, feat.properties?.data] : list),
        [] as DispatchType[],
      );
    } catch (err) {
      return [];
    }
  };
  const selectedPlans = getSelectedPlans();

  const handleBulkAssignSubmit = () => {
    refreshPage();
    handleSetClickBehavior(DEFAULT);
  };

  const handleSetSearchBounds = () => {
    if (map) {
      const bounds = map.getBounds();
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const boundsGeom = JSON.stringify(
        polygon([
          [
            [sw.lng, sw.lat],
            [ne.lng, sw.lat],
            [ne.lng, ne.lat],
            [sw.lng, ne.lat],
            [sw.lng, sw.lat],
          ],
        ]).geometry,
      );
      updateFilter('map_bounds', boundsGeom);
    }
  };

  return (
    <Flex w="100%" direction="column">
      <Box w="100%" h="100%" pos="relative" ref={wrapperRef}>
        <Box ref={mapContainerRef} style={{ width: '100%', height: '40rem' }} />
        {popupData &&
          createPortal(
            <DispatchMapPopup dispatchPlan={popupData} refreshPage={refreshPage} />,
            popupContainerRef.current!,
          )}
        <Paper pos="absolute" top="2rem" left="2rem" w="12rem" p="xs">
          <Stack gap={0}>
            <Text size="sm" w={FONT_WEIGHT_BOLD} td="underline">
              {t('legend')}
            </Text>
            {getPlanTrackingLegendIcons(language).map((legendVal) => (
              <Group justify="space-between" key={legendVal.name}>
                <Text size="xs">{legendVal.name}</Text>
                <Image w="1.5rem" src={legendVal.icon} />
              </Group>
            ))}
          </Stack>
        </Paper>
        <Stack pos="absolute" top="2rem" right="2rem">
          <Button variant="default" onClick={handleSetSearchBounds} disabled={!mapHasLoaded}>
            {t('searchCurrentBounds')}
          </Button>
          {Boolean(filter.map_bounds) && (
            <Button variant="danger" onClick={() => updateFilter('map_bounds', undefined)}>
              {t('removeSearchBounds')}
            </Button>
          )}
        </Stack>
        <Group pos="absolute" bottom="2rem" left="2rem">
          {clickMode === DEFAULT ? (
            <Button variant="default" onClick={() => handleSetClickBehavior(BULK_ASSIGN)}>
              {t('bulkAssign')}
            </Button>
          ) : (
            <>
              <BulkAssignButton
                label={t('clickToAssignPlans')}
                variant="filled"
                dispatchPlans={selectedPlans}
                disabled={selectedPlans.length <= 0}
                onSubmit={handleBulkAssignSubmit}
              />
              <Button
                onClick={() => handleToggleDraw(!isDrawing)}
                variant={isDrawing ? 'filled' : 'default'}
              >
                {t('drawRectangle')}
              </Button>
              <Button onClick={() => handleSetClickBehavior(DEFAULT)} variant="danger">
                {t('cancel')}
              </Button>
            </>
          )}
        </Group>
      </Box>
    </Flex>
  );
};
